import type Phaser from 'phaser';
import type { LevelDef } from '../core/game';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../config/levels';
import { HUB_CHAPTERS, hubChapterOf } from '../config/hubLevels';
import { originChapterOf } from '../config/originEvent';
import { originArenaChapter } from '../config/testArena';
import { EXPLORE_LEVEL_ID } from '../config/creator';
import { exploreRun } from '../config/exploreRun';
import { POTIONS, type PotionKind } from '../config/potions';
import { ProgressService } from '../services/progressService';
import { saveStorage } from './saveStorage';
import { signedInUserId } from '../account/accountUi';

/**
 * Helpers shared by The Hub's screens (spec: 2026-09-02-the-hub-design.md).
 * Render-layer only — the wall clock and the device id both live here, never
 * in src/config.
 */

export const HUB_RED = 0xc0392b;
export const HUB_RED_HEX = '#e8674f';
export const HUB_BLUE = 0x1e5fa8;
export const HUB_BLUE_HEX = '#4aa3e0';
export const GOLD = 0xf5c518;
export const GOLD_HEX = '#f5c518';
/** The Diamond's legendary outline — brighter than the bags' gold. */
export const LEGENDARY_YELLOW = 0xffe94a;
export const LEGENDARY_YELLOW_HEX = '#ffe94a';
/** Stored potions' outline in Storage. */
export const POTION_GREEN = 0x4caf50;
export const POTION_GREEN_HEX = '#7ed67e';

export function hubProgress(): ProgressService {
  return new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
}

/**
 * Where "back to the picker" goes after a level: the hub chapter screen for
 * a hub level or a hub chapter's arena — on THAT chapter, so a Fungi Forest
 * level returns to The Fungi Forest — and the episode picker otherwise.
 */
export function levelPickerFor(level: LevelDef | undefined): { key: string; data: object } {
  // An Explore run goes back to the Explore list; a Level Creator TEST run to
  // the editor, which keeps its project.
  if (level?.creator && level.id === EXPLORE_LEVEL_ID) {
    // A level started from a published chapter goes back to that chapter.
    const chapter = exploreRun()?.chapter;
    if (chapter) return { key: 'ExploreChapterScene', data: { code: chapter.code } };
    return { key: 'ExploreScene', data: {} };
  }
  if (level?.creator) return { key: 'CreatorEditorScene', data: {} };
  const hub = level
    ? (hubChapterOf(level.id) ?? HUB_CHAPTERS.find((c) => c.arenaId === level.id))
    : undefined;
  if (hub) return { key: 'HubChapterScene', data: { chapter: hub.number } };
  // An Origin Event level — or its chapter's Test Arena — returns to that
  // chapter's menu.
  const origin = level
    ? (originChapterOf(level.id)?.id ?? originArenaChapter(level.id))
    : undefined;
  if (origin) return { key: 'OriginChapterScene', data: { chapter: origin } };
  return { key: 'LevelSelectScene', data: level ? { chapter: level.chapter ?? 1 } : {} };
}

const DEVICE_KEY = 'hambi.device';

/**
 * The player's salt: the signed-in account id, else a per-device id minted
 * once and kept in plain localStorage (device-local on purpose — two guests
 * on two devices are two players). Seeds which quest SET a player gets; the
 * Potion Shop's shelf is shared by everyone since Revision 9 and no longer
 * uses it.
 */
export function playerSalt(): string {
  const user = signedInUserId();
  if (user) return user;
  try {
    let id = window.localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'device';
  }
}

/** Label for a potion's targeted-use prompt. */
export function potionUseHint(kind: PotionKind): string {
  const p = POTIONS[kind];
  return p.targeted ? 'Pick the tower it goes on.' : p.effect;
}

/**
 * The shared "USE / STORE" (or "USE / CANCEL") modal — the Storage dialog's
 * layout with the Hub's verbs. `onUse` is handed a `close` that only disposes
 * the dialog; the caller decides what follows.
 */
export function showUseDialog(
  scene: Phaser.Scene,
  opts: {
    title: string;
    subtitle: string;
    accent: number;
    art?: string;
    secondLabel: 'STORE' | 'CANCEL';
    useName?: string;
    secondName?: string;
    onUse: (close: () => void) => void;
    onSecond: (close: () => void) => void;
  },
): void {
  const W = 1280;
  const H = 720;
  const D = 200;
  const objs: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
      .setInteractive()
      .setDepth(D),
  );
  add(
    scene.add
      .rectangle(W / 2, H / 2, 600, 320, 0x1a1726, 1)
      .setStrokeStyle(2, opts.accent)
      .setDepth(D + 1),
  );
  if (opts.art && scene.textures.exists(opts.art)) {
    add(
      scene.add
        .image(W / 2 - 220, H / 2 - 30, opts.art)
        .setDisplaySize(96, 96)
        .setDepth(D + 2),
    );
  }
  const textX = opts.art ? W / 2 + 40 : W / 2;
  add(
    scene.add
      .text(textX, H / 2 - 74, opts.title, {
        fontSize: '28px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D + 2),
  );
  add(
    scene.add
      .text(textX, H / 2 - 30, opts.subtitle, {
        fontSize: '16px',
        color: '#9aa6ad',
        align: 'center',
        wordWrap: { width: opts.art ? 380 : 540 },
      })
      .setOrigin(0.5)
      .setDepth(D + 2),
  );
  const close = () => objs.forEach((o) => o.destroy());
  const mk = (dx: number, text: string, fill: number, stroke: number, name: string) => {
    const box = add(
      scene.add
        .rectangle(W / 2 + dx, H / 2 + 92, 200, 56, fill)
        .setStrokeStyle(2, stroke)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName(name),
    );
    add(
      scene.add
        .text(W / 2 + dx, H / 2 + 92, text, {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    return box;
  };
  mk(-114, 'USE', 0x1a6b1a, 0x4caf50, opts.useName ?? 'use-dialog-use').on('pointerup', () =>
    opts.onUse(close),
  );
  mk(114, opts.secondLabel, 0x2a2a7a, 0x4466cc, opts.secondName ?? 'use-dialog-second').on(
    'pointerup',
    () => opts.onSecond(close),
  );
}

/**
 * Use a potion from `scene`: a targeted potion hands off to TowerPickScene
 * (which comes back to `returnTo` when done, storing the potion if the pick
 * is cancelled); a Bottle'l Burger starts at once. Returns the immediate
 * outcome for the caller's hint — 'picking' means the scene is changing.
 */
export function usePotion(
  scene: Phaser.Scene,
  kind: PotionKind,
  returnTo: string,
  returnData: object = {},
): { outcome: 'started' | 'picking' | 'refused'; reason?: string } {
  const progress = hubProgress();
  if (POTIONS[kind].targeted) {
    scene.scene.start('TowerPickScene', { kind, returnTo, returnData });
    return { outcome: 'picking' };
  }
  const check = progress.startEffect(kind, undefined, Date.now());
  if (!check.ok) {
    // Refused (already running): keep it on the shelf rather than lose it.
    progress.storePotion(kind);
    return { outcome: 'refused', reason: check.reason };
  }
  return { outcome: 'started' };
}

/** Transient one-line hint at the foot of a screen. */
export function flashHint(scene: Phaser.Scene, msg: string, color = '#e8a0a0', y = 690): void {
  const t = scene.add
    .text(640, y, msg, { fontSize: '16px', color, fontStyle: 'italic' })
    .setOrigin(0.5)
    .setDepth(150);
  scene.time.delayedCall(2200, () => t.destroy());
}
